// dependency-graph-ignore-file
import type { MetricNameValue, CanvasMetadata } from '@canvas/frontend-metrics';

import type { FrameState } from './stabilized-scheduler';

// remove this here and let it be merged from the size metrics.
export type MetricMetadataBase = CanvasMetadata & { contentId?: string };

/**
 * Configuration for a stabilized metric.
 *
 * @template TData - Shape of `probeData` passed when firing; used only by `buildProbe`.
 * @template TMetadata - Shape of `metadata` when provided on `fire()`; used by `buildMetadata`.
 */
export interface MetricConfig<
  TData,
  TMetadata extends MetricMetadataBase = MetricMetadataBase
> {
  /** Reliability metric name used when publishing durations for this config. */
  metricName: MetricNameValue;

  /**
   * Builds the per-fire probe: returns a zero-arg function invoked each animation frame until
   * the scheduler completes; see {@link FrameState} in `./stabilized-scheduler`.
   */
  buildProbe: (probeData: TData) => () => FrameState;

  /** Optional mapper from typed `metadata` into the flat metadata object sent to Reliability. */
  buildMetadata?: (metadata: TMetadata) => CanvasMetadata;

  /**
   * Maximum ms from effective `startTime` before `stabilized-metric-timeout`. Omit to use the
   * scheduler default; individual metrics may set a shorter cap when appropriate.
   */
  maxWaitMs?: number;

  /**
   * Consecutive stable frames required for success. Omit to use the scheduler default (2).
   */
  stableFramesRequired?: number;
}

/**
 * Parameters passed when firing a metric.
 *
 * @template TData - Shape of `probeData` used by the metric's probe.
 * @template TMetadata - Shape of `metadata` when provided; optional on every `fire()`.
 *
 * When `metadata` includes `contentId` (string), live database size fields are merged into
 * published reliability metadata; omit it if you do not need that enrichment.
 *
 * **Concurrent fires on the same ref:** A second `fireMetric` while a probe is still running
 * silently cancels the prior rAF loop (no Reliability failure row) and starts the new
 * measurement (see `@databases/frontend-metrics/stabilized-scheduler`).
 */
export interface FireParams<
  TData,
  TMetadata extends MetricMetadataBase = MetricMetadataBase
> {
  /**
   * Start of the measured interval (`performance.now()`), forwarded to the scheduler and
   * clamped there when slightly in the future.
   */
  startTime: number;

  /** Input to `config.buildProbe`; typically DOM handles and ids needed by the probe. */
  probeData: TData;

  /** Typed metadata merged into the published reliability payload when present. */
  metadata?: TMetadata;

  /**
   * Per-fire overrides for scheduler limits. When omitted, `config.maxWaitMs` and
   * `config.stableFramesRequired` apply.
   */
  overrides?: { maxWaitMs?: number; stableFramesRequired?: number };

  /**
   * When set, the scheduler records an immediate failure with this `reason` as `errorMessage`
   * and does not start the rAF loop.
   */
  failImmediately?: { reason: string };
}

/**
 * Factory to define a metric configuration.
 *
 * Returns the config as-is; primarily used to infer generics and provide a clear
 * entry point for metric definitions.
 */
export function defineMetric<
  TData,
  TMetadata extends MetricMetadataBase = MetricMetadataBase
>(config: MetricConfig<TData, TMetadata>): MetricConfig<TData, TMetadata> {
  return config;
}
